import { useEffect, useMemo, useState, type Key } from 'react';
import {
  AutoComplete,
  Button,
  Card,
  Empty,
  Flex,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowRightOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  getPlannerReservations,
  getPlanners,
  movePlannerReservations,
  type Planner,
  type PlannerReservation,
  type ReservationOpenSlot,
} from '../../api/planners';

const { Text, Title } = Typography;

type ListResponse<T> =
  | T[]
  | {
      data?: T[] | { list?: T[]; rows?: T[] };
      list?: T[];
      rows?: T[];
    };

type ApiError = {
  message?: string;
  response?: { data?: { message?: string } };
};

function extractList<T>(response: ListResponse<T>): T[] {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.list)) return response.data.list;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  if (Array.isArray(response?.list)) return response.list;
  if (Array.isArray(response?.rows)) return response.rows;
  return [];
}

function getPlannerName(planner: Planner) {
  return planner.userName || planner.nickname || planner.loginId || `플래너 ${planner.id}`;
}

function getErrorMessage(error: unknown, fallback: string) {
  const apiError = error as ApiError;
  return apiError?.response?.data?.message || apiError?.message || fallback;
}

function formatTimeCode(value: number | string | undefined) {
  if (value === undefined || value === null || value === '') return '-';
  const digits = String(value).padStart(4, '0');
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function getSlotDate(reservation: PlannerReservation) {
  return reservation.openSlots?.[0]?.slotDate || '-';
}

function getSlotSortKey(reservation: PlannerReservation) {
  const slot = reservation.openSlots?.[0];
  if (!slot) return '';
  return `${slot.slotDate || ''}|${String(slot.startTimeCode || '').padStart(4, '0')}|${slot.shopName || ''}`;
}

function compareText(a?: string | null, b?: string | null) {
  return String(a || '').localeCompare(String(b || ''), 'ko');
}

function compareDateTime(a?: string | null, b?: string | null) {
  const aValue = a ? dayjs(a).valueOf() : 0;
  const bValue = b ? dayjs(b).valueOf() : 0;
  return aValue - bValue;
}

function SlotList({ slots }: { slots?: ReservationOpenSlot[] }) {
  if (!slots?.length) {
    return <Text type="secondary">연결된 슬롯 없음</Text>;
  }

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {slots.map((slot) => (
        <Flex key={slot.id} gap={6} align="center" className="planner-transfer-slot">
          <Text ellipsis title={slot.shopName || '-'}>{slot.shopName || '-'}</Text>
          <Text type="secondary">
            {formatTimeCode(slot.startTimeCode)}
          </Text>
        </Flex>
      ))}
    </Space>
  );
}

export default function PlannerReservationTransferPage() {
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [plannerLoading, setPlannerLoading] = useState(false);
  const [sourcePlannerId, setSourcePlannerId] = useState<number>();
  const [targetPlannerId, setTargetPlannerId] = useState<number>();
  const [sourcePlannerQuery, setSourcePlannerQuery] = useState('');
  const [targetPlannerQuery, setTargetPlannerQuery] = useState('');
  const [sourceReservations, setSourceReservations] = useState<PlannerReservation[]>([]);
  const [targetReservations, setTargetReservations] = useState<PlannerReservation[]>([]);
  const [selectedReservationIds, setSelectedReservationIds] = useState<Key[]>([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [recentlyMovedReservationIds, setRecentlyMovedReservationIds] = useState<number[]>([]);

  const plannerOptions = useMemo(
    () =>
      planners.map((planner) => {
        const name = getPlannerName(planner);
        const extraInfo = [planner.nickname !== planner.userName ? planner.nickname : null, planner.loginId]
          .filter(Boolean)
          .join(' / ');

        return {
          key: String(planner.id),
          value: String(planner.id),
          label: (
            <Flex justify="space-between" gap={12}>
              <Text>{name}</Text>
              <Text type="secondary">
                {extraInfo ? `${extraInfo} · ID ${planner.id}` : `ID ${planner.id}`}
              </Text>
            </Flex>
          ),
          plannerId: planner.id,
          plannerName: name,
          searchText: [planner.userName, planner.nickname, planner.loginId, planner.id]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        };
      }),
    [planners],
  );

  const targetDisplayReservations = useMemo(() => {
    if (!recentlyMovedReservationIds.length) return targetReservations;

    const movedIds = new Set(recentlyMovedReservationIds);

    return [...targetReservations].sort((a, b) => {
      const aMoved = movedIds.has(a.id) ? 1 : 0;
      const bMoved = movedIds.has(b.id) ? 1 : 0;

      if (aMoved !== bMoved) return bMoved - aMoved;
      return getSlotDate(a).localeCompare(getSlotDate(b));
    });
  }, [recentlyMovedReservationIds, targetReservations]);

  const targetPlanner = useMemo(
    () => planners.find((planner) => planner.id === targetPlannerId),
    [planners, targetPlannerId],
  );

  const loadPlanners = async () => {
    setPlannerLoading(true);

    try {
      const response = (await getPlanners()) as ListResponse<Planner>;
      setPlanners(extractList(response));
    } catch (error: unknown) {
      message.error(getErrorMessage(error, '플래너 목록을 불러오지 못했습니다.'));
    } finally {
      setPlannerLoading(false);
    }
  };

  const loadReservations = async (plannerId: number, type: 'source' | 'target') => {
    const setLoading = type === 'source' ? setSourceLoading : setTargetLoading;
    const setReservations = type === 'source' ? setSourceReservations : setTargetReservations;

    setLoading(true);

    try {
      const response = (await getPlannerReservations(plannerId)) as ListResponse<PlannerReservation>;
      const reservations = extractList(response).filter(
        (reservation) => reservation.reservationStatus !== 'CANCELED',
      );

      setReservations(reservations);
      return reservations;
    } catch (error: unknown) {
      setReservations([]);
      message.error(getErrorMessage(error, '예약 목록을 불러오지 못했습니다.'));
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanners();
  }, []);

  const handleSourceSelect = (value: string, plannerId: number) => {
    setSourcePlannerQuery(value);
    setSourcePlannerId(plannerId);
    setSelectedReservationIds([]);
    setRecentlyMovedReservationIds([]);

    if (plannerId === targetPlannerId) {
      setTargetPlannerId(undefined);
      setTargetPlannerQuery('');
      setTargetReservations([]);
    }

    loadReservations(plannerId, 'source');
  };

  const handleTargetSelect = (value: string, plannerId: number) => {
    if (plannerId === sourcePlannerId) {
      message.warning('보내는 플래너와 받는 플래너는 같을 수 없습니다.');
      setTargetPlannerQuery('');
      setTargetPlannerId(undefined);
      setTargetReservations([]);
      setRecentlyMovedReservationIds([]);
      return;
    }

    setTargetPlannerQuery(value);
    setTargetPlannerId(plannerId);
    setRecentlyMovedReservationIds([]);
    loadReservations(plannerId, 'target');
  };

  const handleMove = async () => {
    if (!sourcePlannerId || !targetPlannerId || selectedReservationIds.length === 0) return;

    const confirmed = window.confirm('최종적으로 확인하셨습니까?');
    if (!confirmed) return;

    const reservationIds = selectedReservationIds.map(Number);

    setMoving(true);

    try {
      await movePlannerReservations(targetPlannerId, { reservationIds });

      await Promise.all([
        loadReservations(sourcePlannerId, 'source'),
        loadReservations(targetPlannerId, 'target'),
      ]);

      setRecentlyMovedReservationIds(reservationIds);
      setSelectedReservationIds([]);

      const targetPlannerName = targetPlanner ? getPlannerName(targetPlanner) : `플래너 ${targetPlannerId}`;
      message.success(`${reservationIds.length}개의 예약을 ${targetPlannerName} 플래너에게 이동했습니다.`);
    } catch (error: unknown) {
      message.error(getErrorMessage(error, '예약 이동 중 오류가 발생했습니다.'));
    } finally {
      setMoving(false);
    }
  };

  const sourceColumns: ColumnsType<PlannerReservation> = [
    {
      title: '신부명',
      dataIndex: 'customerName',
      width: 100,
      sorter: (a, b) => compareText(a.customerName, b.customerName),
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: '투어 날짜',
      width: 105,
      sorter: (a, b) => getSlotDate(a).localeCompare(getSlotDate(b)),
      render: (_, reservation) => getSlotDate(reservation),
    },
    {
      title: '예약 슬롯',
      width: 170,
      sorter: (a, b) => getSlotSortKey(a).localeCompare(getSlotSortKey(b), 'ko'),
      render: (_, reservation) => <SlotList slots={reservation.openSlots} />,
    },
    {
      title: '예약 생성일',
      dataIndex: 'reservedTime',
      width: 105,
      sorter: (a, b) => compareDateTime(a.reservedTime, b.reservedTime),
      defaultSortOrder: 'descend',
      render: (value: string) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
  ];

  const targetColumns: ColumnsType<PlannerReservation> = [
    {
      title: '상태',
      width: 88,
      sorter: (a, b) => {
        const aMoved = recentlyMovedReservationIds.includes(a.id) ? 1 : 0;
        const bMoved = recentlyMovedReservationIds.includes(b.id) ? 1 : 0;
        return bMoved - aMoved;
      },
      render: (_, reservation) => (
        recentlyMovedReservationIds.includes(reservation.id)
          ? <Tag color="blue">이동 완료</Tag>
          : <Text type="secondary">-</Text>
      ),
    },
    {
      title: '신부명',
      dataIndex: 'customerName',
      width: 100,
      sorter: (a, b) => compareText(a.customerName, b.customerName),
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: '투어 날짜',
      width: 105,
      sorter: (a, b) => getSlotDate(a).localeCompare(getSlotDate(b)),
      render: (_, reservation) => getSlotDate(reservation),
    },
    {
      title: '예약 슬롯',
      width: 170,
      sorter: (a, b) => getSlotSortKey(a).localeCompare(getSlotSortKey(b), 'ko'),
      render: (_, reservation) => <SlotList slots={reservation.openSlots} />,
    },
    {
      title: '예약 생성일',
      dataIndex: 'reservedTime',
      width: 105,
      sorter: (a, b) => compareDateTime(a.reservedTime, b.reservedTime),
      render: (value: string) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
  ];

  const canMove = Boolean(
    sourcePlannerId && targetPlannerId && selectedReservationIds.length > 0,
  );

  return (
    <Flex vertical gap={20} className="planner-transfer-page">
      <div>
        <Title level={3} style={{ margin: 0 }}>플래너 예약 이동</Title>
        <Text type="secondary">보내는 플래너의 예약을 선택해 다른 플래너에게 이동합니다.</Text>
      </div>

      <Flex gap={16} align="stretch" className="planner-transfer-row">
        <div className="planner-transfer-card-column">
          <Card
            className="planner-transfer-card"
            title={
              <Space>
                <span>보내는 플래너</span>
                {sourcePlannerId && <Tag>{sourceReservations.length}건</Tag>}
              </Space>
            }
            extra={
              <Button
                icon={<ReloadOutlined />}
                disabled={!sourcePlannerId}
                onClick={() => sourcePlannerId && loadReservations(sourcePlannerId, 'source')}
              >
                새로고침
              </Button>
            }
          >
            <div className="planner-transfer-card-content">
              <AutoComplete
                allowClear
                showSearch
                options={plannerOptions}
                placeholder="보내는 플래너를 검색하세요"
                loading={plannerLoading}
                value={sourcePlannerQuery}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  String(option?.searchText || '').includes(input.toLowerCase())
                }
                onChange={(value) => {
                  setSourcePlannerQuery(value);

                  if (sourcePlannerId) {
                    setSourcePlannerId(undefined);
                    setSourceReservations([]);
                    setSelectedReservationIds([]);
                    setRecentlyMovedReservationIds([]);
                  }
                }}
                onSelect={(_, option) =>
                  handleSourceSelect(String(option.plannerName), Number(option.plannerId))
                }
                onClear={() => {
                  setSourcePlannerQuery('');
                  setSourcePlannerId(undefined);
                  setSourceReservations([]);
                  setSelectedReservationIds([]);
                  setRecentlyMovedReservationIds([]);
                }}
              />

              <div className="planner-transfer-table-region">
                <Table
                  className="planner-transfer-table"
                  rowKey="id"
                  size="middle"
                  loading={sourceLoading}
                  columns={sourceColumns}
                  dataSource={sourceReservations}
                  pagination={false}
                  locale={{
                    emptyText: sourcePlannerId
                      ? <Empty description="이동할 수 있는 예약이 없습니다." />
                      : <Empty description="보내는 플래너를 먼저 선택하세요." />,
                  }}
                  rowSelection={{
                    selectedRowKeys: selectedReservationIds,
                    onChange: (keys) => {
                      setSelectedReservationIds(keys);
                      setRecentlyMovedReservationIds([]);
                    },
                    preserveSelectedRowKeys: false,
                  }}
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="planner-transfer-action-column">
          <Flex vertical align="center" justify="center" gap={6}>
            <Text strong>{selectedReservationIds.length}건</Text>
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              loading={moving}
              disabled={!canMove}
              onClick={handleMove}
            >
              이동
            </Button>
          </Flex>
        </div>

        <div className="planner-transfer-card-column">
          <Card
            className="planner-transfer-card"
            title={
              <Space>
                <span>받는 플래너</span>
                {targetPlannerId && <Tag>{targetReservations.length}건</Tag>}
                {recentlyMovedReservationIds.length > 0 && (
                  <Tag color="blue">방금 이동 {recentlyMovedReservationIds.length}건</Tag>
                )}
              </Space>
            }
            extra={
              <Button
                icon={<ReloadOutlined />}
                disabled={!targetPlannerId}
                onClick={() => targetPlannerId && loadReservations(targetPlannerId, 'target')}
              >
                새로고침
              </Button>
            }
          >
            <div className="planner-transfer-card-content">
              <AutoComplete
                allowClear
                showSearch
                options={plannerOptions}
                placeholder="받는 플래너를 검색하세요"
                loading={plannerLoading}
                value={targetPlannerQuery}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  String(option?.searchText || '').includes(input.toLowerCase())
                }
                onChange={(value) => {
                  setTargetPlannerQuery(value);

                  if (targetPlannerId) {
                    setTargetPlannerId(undefined);
                    setTargetReservations([]);
                    setRecentlyMovedReservationIds([]);
                  }
                }}
                onSelect={(_, option) =>
                  handleTargetSelect(String(option.plannerName), Number(option.plannerId))
                }
                onClear={() => {
                  setTargetPlannerQuery('');
                  setTargetPlannerId(undefined);
                  setTargetReservations([]);
                  setRecentlyMovedReservationIds([]);
                }}
              />

              <div className="planner-transfer-table-region">
                <Table
                  className="planner-transfer-table"
                  rowKey="id"
                  size="middle"
                  loading={targetLoading}
                  columns={targetColumns}
                  dataSource={targetDisplayReservations}
                  pagination={false}
                  rowClassName={(reservation) =>
                    recentlyMovedReservationIds.includes(reservation.id)
                      ? 'planner-transfer-moved-row'
                      : ''
                  }
                  locale={{
                    emptyText: targetPlannerId
                      ? <Empty description="받는 플래너의 예약이 없습니다." />
                      : <Empty description="받는 플래너를 먼저 선택하세요." />,
                  }}
                />
              </div>
            </div>
          </Card>
        </div>
      </Flex>

      <style>{`
        .planner-transfer-page {
          height: calc(100vh - 40px);
          max-height: calc(100vh - 40px);
          min-height: 0;
          overflow: hidden;
        }

        .planner-transfer-row {
          flex: 1;
          height: 0;
          min-height: 0;
          overflow: hidden;
        }

        .planner-transfer-card-column {
          display: flex;
          flex: 1 1 0;
          width: 0;
          min-width: 0;
          height: 100%;
        }

        .planner-transfer-action-column {
          display: flex;
          flex: 0 0 120px;
          min-width: 120px;
          align-items: center;
          justify-content: center;
          height: 100%;
        }

        .planner-transfer-card {
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
          min-height: 0;
          overflow: hidden;
        }

        .planner-transfer-card > .ant-card-body {
          display: flex;
          flex: 1;
          min-height: 0;
          flex-direction: column;
          overflow: hidden;
        }

        .planner-transfer-card-content {
          display: flex;
          flex: 1;
          min-height: 0;
          flex-direction: column;
          gap: 16px;
          width: 100%;
          overflow: hidden;
        }

        .planner-transfer-table-region {
          flex: 1;
          min-height: 0;
          overflow: auto;
          overscroll-behavior: contain;
        }

        .planner-transfer-slot {
          min-width: 0;
          white-space: nowrap;
        }

        .planner-transfer-slot > .ant-typography:first-child {
          min-width: 0;
          max-width: 108px;
        }

        .planner-transfer-moved-row > td {
          background: #e6f4ff !important;
        }

        .planner-transfer-moved-row:hover > td {
          background: #d6efff !important;
        }

        @media (max-width: 1199px) {
          .planner-transfer-row {
            flex-direction: column;
            overflow-y: auto;
          }

          .planner-transfer-card-column {
            flex: none;
            width: 100%;
            height: calc(100vh - 120px);
          }

          .planner-transfer-action-column {
            flex: 0 0 auto;
            width: 100%;
            min-width: 0;
            min-height: 88px;
            height: auto;
          }
        }
      `}</style>
    </Flex>
  );
}
